import React, {
  type ComponentProps,
  type MouseEvent,
  type MouseEventHandler,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
} from 'react'
import { TransitionGroup } from 'react-transition-group'

import { UnstyledButton } from '../../../components/UnstyledButton'
import { CssVars, NeutralColorToken, SemanticColorToken } from '../../themes'
import { Card } from '../Card'
import { FoundationIcon } from '../FoundationIcon'
import type { Icon24 } from '../FoundationIcon/icons'
import { TextButton } from '../TextButton'
import { TextColorOverride } from '../TextColorOverride'
import { Type } from '../Type'
import { ToastContext } from './ToastContext'
import { TOAST_HEIGHT, ToastTransition } from './ToastTransition'
import type { AriaRole, Entry, TAction, Variant } from './toastTypes'

const iconByVariant: { [k in Variant]: Icon24 | undefined } = {
  default: undefined,
  error: 'AlertFilled24',
  success: 'BubbleChecked24',
}

function ActionButton({
  action,
  onClose,
}: {
  action: TAction
  onClose: () => void
}) {
  const { onClick, ...props } = action

  return (
    <TextButton
      {...props}
      color={NeutralColorToken.Fg1}
      isLegacyStyle
      onClick={<T extends unknown>(e: MouseEvent<T>) => {
        if (action.type === 'button') {
          ;(onClick as MouseEventHandler<HTMLButtonElement>)?.(
            e as MouseEvent<HTMLButtonElement>
          )
        } else {
          ;(onClick as MouseEventHandler<HTMLAnchorElement>)?.(
            e as MouseEvent<HTMLAnchorElement>
          )
        }

        onClose()
      }}
    />
  )
}

function Toast({
  duration,
  onClose,
  ...props
}: Omit<
  ComponentProps<typeof ToastUI>,
  'onMouseEnter' | 'onMouseLeave' | 'onFocus' | 'onBlur'
> & {
  duration: number
}) {
  const onCloseRef = useRef<() => void>(undefined)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  const isHoveredRef = useRef(false)
  const isFocusedRef = useRef(false)

  const timeoutRef = useRef<number>(undefined)
  function startCloseTimer() {
    // NOTE(jare): We currently don't bother watching for changes to
    // duration prop. Whatever it's mounted with is what it'll be.
    window.clearTimeout(timeoutRef.current)
    timeoutRef.current = window.setTimeout(() => {
      if (onCloseRef.current) {
        onCloseRef.current()
      }
    }, duration)
  }
  useEffect(() => {
    if (!isHoveredRef.current && !isFocusedRef.current) {
      startCloseTimer()
    }

    return () => window.clearTimeout(timeoutRef.current)
    // Note (@auster.chen): this will only be run the first time, don't need to depend on startCloseTimer
    // NOTE (david.aghassi) this was disabled during the upgrade of eslint-plugin-react-hooks.
    // Or was a pre-existing offense
    // Please refactor and solve the issue next time this code is touched
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // NOTE(jare): Ensure that the toast doesn't automatically close while someone's
  // mouse is hovered over it
  const handleMouseEnter = () => {
    window.clearTimeout(timeoutRef.current)
    isHoveredRef.current = true
  }

  const handleMouseLeave = () => {
    isHoveredRef.current = false
    if (!isFocusedRef.current) {
      startCloseTimer()
    }
  }

  // In React, "focus" and "blur" events bubble up, even though the
  // native focus and blur events don't. That means there's no need
  // to use "focusin" and "focusout" here.
  // NOTE(jare): Ensure that the toast doesn't automatically close while the user is
  // focused on it
  // Note(@auster.chen) React 17 will map focus, blur to focusin and focusout
  const handleFocus = () => {
    isFocusedRef.current = true
    window.clearTimeout(timeoutRef.current)
  }

  const handleBlur = () => {
    isFocusedRef.current = false
    if (!isHoveredRef.current) {
      startCloseTimer()
    }
  }

  return (
    <ToastUI
      onClose={onClose}
      {...props}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    />
  )
}

function ToastUI({
  action,
  children,
  icon,
  onBlur,
  onClose,
  onDismiss,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  role,
  variant = 'default',
}: {
  role: AriaRole
  icon?: Icon24
  action?: TAction
  children: ReactNode
  onClose: () => void
  onDismiss?: () => void
  onMouseEnter: () => void
  onMouseLeave: () => void
  onFocus: () => void
  onBlur: () => void
  variant?: Variant
}) {
  const hasCloseButton = !!onClose

  return (
    <TextColorOverride color={NeutralColorToken.Bg1}>
      {({ rootClassName }) => (
        <div
          // NOTE(jare): We only add a redundant aria-live if the role isn't set to
          // "alert". On iOS's VoiceOver, setting both aria-live="assertive" and
          // role="alert" causes double speaking issues
          // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions
          aria-live={role !== 'alert' ? 'polite' : undefined}
          className={rootClassName}
          onBlur={onBlur}
          onFocus={onFocus}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          role={role}
        >
          <Card
            css={{
              background: CssVars.neutral.bg1,
              height: TOAST_HEIGHT,
              paddingBottom: 12,
              paddingLeft: 16,
              paddingRight: 12,
              paddingTop: 12,
            }}
          >
            <div
              css={{
                alignItems: 'center',
                display: 'flex',
                height: '100%',
              }}
            >
              {icon && (
                <div
                  css={{
                    display: 'flex',
                    flexShrink: 0,
                    marginRight: CssVars.size.gutterWidth,
                  }}
                >
                  <FoundationIcon
                    color={
                      variant === 'success'
                        ? SemanticColorToken.Positive
                        : SemanticColorToken.Negative
                    }
                    type={icon}
                  />
                </div>
              )}

              <div>{children}</div>
              <div
                css={{
                  alignItems: 'center',
                  display: 'flex',
                  flexShrink: 0,
                  marginLeft: 'auto',
                }}
              >
                {action && (
                  <div css={{ marginLeft: 16 }}>
                    <ActionButton action={action} onClose={onClose} />
                  </div>
                )}
                {hasCloseButton && !!onClose && (
                  <div css={{ marginLeft: action ? 4 : 16 }}>
                    <UnstyledButton
                      css={{
                        alignItems: 'center',
                        cursor: 'pointer',
                        display: 'flex',
                        padding: '8px',
                      }}
                      onClick={() => {
                        onDismiss?.()
                        onClose()
                      }}
                    >
                      <FoundationIcon type="Close16" />
                    </UnstyledButton>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}
    </TextColorOverride>
  )
}

export function ToastItems({
  entries,
  remove,
}: {
  entries: Array<Entry>
  remove: (id: string) => void
}) {
  const context = useContext(ToastContext)

  if (!context) {
    throw new Error('<ToastItems> must be wrapped inside <ToastProvider>')
  }

  return (
    <TransitionGroup>
      {entries.map((entry, index) => (
        <ToastTransition
          key={entry.id}
          index={index}
          numToasts={entries.length}
          variant={entry.variant}
        >
          <Toast
            action={entry.action || undefined}
            duration={entry.duration}
            icon={iconByVariant[entry.variant] || undefined}
            onClose={() => {
              remove(entry.id)
            }}
            onDismiss={entry.onDismiss}
            role={entry.role}
            variant={entry.variant}
          >
            <Type.Text
              maxLines={context.horizontalAlignment === 'center' ? 1 : 2}
              size="S"
            >
              {entry.content}
            </Type.Text>
          </Toast>
        </ToastTransition>
      ))}
    </TransitionGroup>
  )
}
